// MoneyTextFieldRange.tsx
import * as React from 'react';
import { Box } from '@mui/material';
import { MoneyTextField } from './MoneyInput';
import { MoneyTextFieldRangeProps } from './types';
import { showWarningSnackbar } from '../../../snackBar/snakbar';

type Props = MoneyTextFieldRangeProps & {
  /** If false, skip min<max validation entirely */
  enforceOrder?: boolean;
  /** If true, width adjusts based on character count */
  dynamicWidth?: boolean;
  /** If true, removes border from input fields */
  disableBorder?: boolean;
  /** If true, removes outline from input fields */
  disableOutline?: boolean;
};

const isEmptyVal = (val: unknown) => val == null || val === '' || val === 0;

export const MoneyTextFieldRange: React.FC<Props> = ({
  value,
  onChange,
  currencySymbol = '₹',
  locale = 'en-IN',
  enforceOrder = false,
  dynamicWidth = true,
}) => {
  const [minVal, maxVal] = value;
  
  // Ref to store timeout IDs for debouncing
  const minWarningTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWarningTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateWidth = React.useCallback((val: number | null) => {
    if (!dynamicWidth) return 140;
    const charCount = String(val || 0).length;
    const minWidth = 10;
    return Math.max(minWidth, charCount + 7) * 8;
  }, [dynamicWidth]);

  const getInputStyles = React.useCallback((val: number | null) => ({
    minWidth: calculateWidth(val)
  }), [calculateWidth]);

  const setMin = React.useCallback(
    (v: number | null) => {
      if (!enforceOrder) {
        onChange([v, maxVal]);
        return;
      }
      
      // Clear any existing timeout for min warning
      if (minWarningTimeoutRef.current) {
        clearTimeout(minWarningTimeoutRef.current);
        minWarningTimeoutRef.current = null;
      }
      
      if (isEmptyVal(maxVal) || v == null || v < (maxVal as number)) {
        onChange([v, maxVal]);
      } else {
        // Debounce the warning message
        minWarningTimeoutRef.current = setTimeout(() => {
          showWarningSnackbar('Minimum value must be less than Maximum value');
        }, 1000);
      }
    },
    [enforceOrder, maxVal, onChange]
  );

  const setMax = React.useCallback(
    (v: number | null) => {
      if (!enforceOrder) {
        onChange([minVal, v]);
        return;
      }
      
      // Clear any existing timeout for max warning
      if (maxWarningTimeoutRef.current) {
        clearTimeout(maxWarningTimeoutRef.current);
        maxWarningTimeoutRef.current = null;
      }
      
      if (isEmptyVal(minVal) || v == null || v > (minVal as number)) {
        onChange([minVal, v]);
      } else {
        // Debounce the warning message
        maxWarningTimeoutRef.current = setTimeout(() => {
          showWarningSnackbar('Maximum value must be greater than Minimum value');
        }, 1000);
      }
    },
    [enforceOrder, minVal, onChange]
  );

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (minWarningTimeoutRef.current) {
        clearTimeout(minWarningTimeoutRef.current);
      }
      if (maxWarningTimeoutRef.current) {
        clearTimeout(maxWarningTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <MoneyTextField
        value={minVal}
        onChange={setMin}
        currencySymbol={currencySymbol}
        locale={locale}
        fullWidth={false}
        sx={getInputStyles(minVal)}
      />
      <span>to</span>
      <MoneyTextField
        value={maxVal}
        onChange={setMax}
        currencySymbol={currencySymbol}
        locale={locale}
        fullWidth={false}
        sx={getInputStyles(maxVal)}
      />
    </Box>
  );
};
